import PhoneNumberInput, {
  type OnChangeDetails,
} from '@components/PhoneNumberInput/PhoneNumberInput';
import Select, { Option } from '@components/Select/Select';
import Tooltip from '@components/Tooltip/Tooltip';
import { editedCardFieldsContext } from '@context/edited-card-fields-context';
import { notificationContext } from '@context/notifications';
import { PopoverContext } from '@context/popover-context';
import { useUser } from '@context/user-context';
import { faLink } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useNoRenderRef from '@hooks/use-no-render-ref';
import useNode from '@hooks/use-node';
import useSyncWhile from '@hooks/use-sync-while';
import {
  autocorrectURLValue,
  findPartialOrCompleteType,
  getDefaultUrlValue,
  getDefaultUrlValueLabel,
  getSuggestionsByFieldType,
  getSuggestionsFromURLFieldValue,
  getURIFromURLFieldValue,
} from '@modules/card-helpers';
import { formatUrlType } from '@modules/formatters';
import { TextField } from '@ui-lib';
import { type CountryCode, isSupportedCountry, parsePhoneNumber } from 'libphonenumber-js';
import { AnimatePresence, motion } from 'motion/react';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import type { CardPresetUrl, CardUrl, CardUrlType } from 'types';

import LinkStatusContainer from '../LinkStatusContainer/LinkStatusContainer';
import SuggestionsSection, {
  useSuggestionsSection,
} from '../SuggestionsSection/SuggestionsSection';
import styles from './UrlEditPopover.module.scss';

const urlTypes = [
  'org',
  'calendar',
  'calendly',
  'linkedin',
  'instagram',
  'threads',
  'twitter',
  'twitterX',
  'facebook',
  'youtube',
  'snapchat',
  'whatsapp',
  'paypal',
  'discord',
  'twitch',
  'telegram',
  'skype',
  'tiktok',
  'github',
  'yelp',
  'venmo',
  'cashapp',
  'signal',
  'link',
] as const;

function useFocusMainInputOnShow(inputNode: HTMLInputElement | undefined) {
  const { show } = useContext(PopoverContext);

  useEffect(() => {
    if (show && inputNode) {
      const intersectionObserver = new IntersectionObserver((entries, observer) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setTimeout(() => {
            inputNode.focus();
          }, 150);
          observer.unobserve(entry.target);
        }
      });

      intersectionObserver.observe(inputNode);
    }
  }, [show, inputNode]);
}

interface UseUrlEditorParams<T> {
  field: T;
  onChange: (field: T, reason: 'type' | 'title') => void;
}

function useUrlEditor<T extends CardUrl | CardPresetUrl>({
  field,
  onChange,
}: UseUrlEditorParams<T>) {
  const suggestions = useMemo(() => {
    const derivedSuggestions = getSuggestionsFromURLFieldValue(field.type, field.userValue);
    const baseSuggestions = getSuggestionsByFieldType('url')(field.type);
    return [...baseSuggestions, ...derivedSuggestions];
  }, [field.type, field.userValue]);

  const [selectedOption, setSelectedOption] = useSuggestionsSection(field.title, suggestions);
  const onChangeRef = useNoRenderRef(onChange);

  const handleTitleChange = useCallback(
    (title: string) => {
      onChangeRef.current({ ...field, title }, 'title');
    },
    [field, onChangeRef],
  );

  const handleTypeChange = useCallback(
    (type: string) => {
      const newValue = getDefaultUrlValue(type as CardUrl['type']);
      const hasUsedSuggestionForTitle = suggestions.some(
        (suggestion) => suggestion === field.title,
      );

      if (hasUsedSuggestionForTitle) {
        onChangeRef.current(
          {
            ...field,
            type: type as CardUrl['type'],
            title: '',
            value: '',
            userValue: newValue,
          },
          'type',
        );
      } else {
        onChangeRef.current(
          {
            ...field,
            type: type as CardUrl['type'],
            value: '',
            userValue: newValue,
          },
          'type',
        );
      }
    },
    [field, onChangeRef, suggestions],
  );

  const handleSelectSuggestion = useCallback(
    (suggestion: string) => {
      const newSelectedOption = suggestion === selectedOption ? '' : suggestion;

      setSelectedOption(newSelectedOption);
      onChangeRef.current({ ...field, title: newSelectedOption }, 'title');
    },
    [field, onChangeRef, selectedOption, setSelectedOption],
  );

  const props = useMemo(
    () => ({
      field,
      suggestions,
      selectedOption,
      onTitleChange: handleTitleChange,
      onTypeChange: handleTypeChange,
      onSelectSuggestion: handleSelectSuggestion,
    }),
    [
      field,
      suggestions,
      selectedOption,
      handleTitleChange,
      handleTypeChange,
      handleSelectSuggestion,
    ],
  );

  return props;
}

export interface UrlEditContainerProps<T> {
  field: T;
  onChange: (field: T) => void;
  linked?: boolean;
  mode: 'create' | 'update' | undefined;
}

export function UrlEditContainer<T extends CardUrl | CardPresetUrl>(
  props: UrlEditContainerProps<T>,
): ReactElement {
  switch (props.field.type) {
    case 'whatsapp':
    case 'signal':
      return <PhoneNumberBasedUrlEditor {...props} />;
    default:
      return <BasicUrlEditor {...props} />;
  }
}

function BasicUrlEditor<T extends CardUrl | CardPresetUrl>({
  field,
  onChange,
  linked = false,
  mode,
}: UrlEditContainerProps<T>): ReactElement {
  const [showURLPreview, setShowURLPreview] = useState(mode === 'update');
  const valueInputId = useId();
  const [valueInputNode, valueInputRef] = useNode<HTMLInputElement>();
  const { onEditAttribute } = useContext(editedCardFieldsContext);
  const { notify } = useContext(notificationContext);
  useFocusMainInputOnShow(valueInputNode);

  const urlEditorProps = useUrlEditor({
    field,
    onChange: (field, reason) => {
      if (reason === 'type') {
        onChange({ ...field, userValue: field.value });
      }
      onChange(field);
    },
  });

  const handleValueChange = useCallback(
    (value: string) => {
      /** Autocorrect the URL value based on the previous state. */
      const correctedValue: string = autocorrectURLValue(value, field.type);

      /** Calendar accepts any scheduling provider without changing its field type. */
      const detectedType =
        field.type === 'calendar' ? undefined : findPartialOrCompleteType(correctedValue);
      const usedType: CardUrlType = detectedType ?? field.type;
      const standardizedURLValue = getURIFromURLFieldValue(usedType, correctedValue);
      onEditAttribute(field.id);
      onChange({
        ...field,
        value: standardizedURLValue,
        userValue: correctedValue,
        type: usedType,
      });
    },

    [field, onChange, onEditAttribute],
  );

  const handleInputBlur = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  const handleInputPaste = useCallback(() => {
    setShowURLPreview(true);
  }, []);

  return (
    <UrlEditor {...urlEditorProps}>
      <LinkStatusContainer linked={linked}>
        <div className={styles['value-input-container']}>
          <TextField
            id={valueInputId}
            ref={valueInputRef}
            value={field.userValue}
            onChange={(event) => handleValueChange(event.currentTarget.value)}
            label={getDefaultUrlValueLabel(field.type)}
            onBlur={handleInputBlur}
            onPaste={handleInputPaste}
          />
          <div className={styles['url-preview']} data-visible={field.value && showURLPreview}>
            <AnimatePresence>
              {field.value && showURLPreview && (
                <Tooltip
                  size="small"
                  label={`This is the URL we will send people to when they click your ${formatUrlType(
                    field.type,
                  )} field`}
                  aria-label="URL Preview message"
                >
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 0.2 } }}
                    exit={{ opacity: 0, transition: { duration: 0.2 } }}
                    className={styles['url-preview-content-container']}
                    onClick={(event) => {
                      event.stopPropagation();
                      navigator.clipboard.writeText(field.value).then(() => {
                        notify({
                          id: 'clipboard-success',
                          type: 'info',
                          title: 'Copied to clipboard',
                          size: 'small',
                          timeout: 1500,
                        });
                      });
                    }}
                    type="button"
                  >
                    <span className={styles['url-preview-icon']}>
                      <FontAwesomeIcon icon={faLink} />
                    </span>
                    <span className={styles['url-preview-value']}>{field.value}</span>
                  </motion.button>
                </Tooltip>
              )}
            </AnimatePresence>
          </div>
        </div>
      </LinkStatusContainer>
    </UrlEditor>
  );
}

function PhoneNumberBasedUrlEditor<T extends CardUrl | CardPresetUrl>({
  field,
  onChange,
  linked = false,
}: UrlEditContainerProps<T>): ReactElement {
  const [countryCode, setCountryCode] = useState<CountryCode>();
  const [phoneNumber, setPhoneNumber] = useState('');
  const { user } = useUser();
  const [valueInputNode, valueInputRef] = useNode<HTMLInputElement>();
  const { onEditAttribute, editedAttributes } = useContext(editedCardFieldsContext);
  const hasBeenEdited = editedAttributes.includes(field.id);

  useSyncWhile(
    field,
    (field) => {
      if (field.userValue) {
        // Older phone numbers may be localised, which will cause parsePhoneNumber to throw an error
        try {
          const phoneNumber = parsePhoneNumber(field.userValue);
          setCountryCode(phoneNumber.country);
          setPhoneNumber(field.userValue.split(`+${phoneNumber.countryCallingCode}`)[1]);
        } catch {
          // In the case of a localised number try and fill in the country code with data we have on the user
          if (user?.country && isSupportedCountry(user.country)) {
            setCountryCode(user.country as CountryCode);
          }

          setPhoneNumber(field.userValue);
        }
      }
    },
    !hasBeenEdited,
  );
  useFocusMainInputOnShow(valueInputNode);

  const urlEditorProps = useUrlEditor({
    field,
    onChange,
  });

  const handleValueChange = useCallback(
    (details: OnChangeDetails) => {
      setPhoneNumber(details.number);
      setCountryCode(details.countryCode);
      onEditAttribute(field.id);

      const standardizedURLValue = getURIFromURLFieldValue(field.type, details.internationalNumber);

      onChange({
        ...field,
        value: standardizedURLValue,
        userValue: details.preservedNumber,
      });
    },
    [field, onChange, onEditAttribute],
  );

  return (
    <UrlEditor {...urlEditorProps}>
      <div className={styles['phone-number-input']}>
        <LinkStatusContainer linked={linked}>
          <PhoneNumberInput
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            onChange={handleValueChange}
            ref={valueInputRef}
          />
        </LinkStatusContainer>
      </div>
    </UrlEditor>
  );
}

type UrlEditorProps = ReturnType<typeof useUrlEditor> & { children: ReactNode };

function UrlEditor({
  children,
  field,
  selectedOption,
  suggestions,
  onTitleChange,
  onTypeChange,
  onSelectSuggestion,
}: UrlEditorProps): ReactElement {
  const titleInputId = useId();
  // hides the suggestions if any of the suggestions are longer than 60 characters
  const titleSuggestions = suggestions.filter((suggestion) => suggestion.length < 60);

  return (
    <div className={styles['link-fields-edit-popover']}>
      <div className={styles['card-edit-select-container']}>
        <span className={styles['type-label']}>Type:</span>
        <div className={styles['card-edit-select']}>
          <Select onSelect={onTypeChange} value={field.type} size="small">
            {urlTypes.map((urlType) => (
              <Option value={urlType} key={urlType}>
                {formatUrlType(urlType)}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className={styles['card-edit-input-container']}>{children}</div>
      <div className={styles['card-edit-input-container']}>
        <TextField
          id={titleInputId}
          value={field.title}
          onChange={(event) => onTitleChange(event.currentTarget.value)}
          label="Title"
        />
      </div>

      <div className={styles['suggestions-section-container']}>
        <SuggestionsSection
          options={titleSuggestions}
          onSelect={onSelectSuggestion}
          selectedOption={selectedOption}
          subject="title"
        />
      </div>
    </div>
  );
}

export default UrlEditContainer;
